import {
    DatePicker,
    Form as AntForm,
    Input,
    Space,
} from 'antd';
import {DatePickerProps} from 'antd/es/date-picker';
import {FormInstance} from 'antd/es/form';
import Radio, {RadioChangeEvent} from 'antd/es/radio';
import moment, {Moment} from 'moment';
import React, {
    FC, useState, useEffect,
} from 'react';

import {selectTableEntityData} from 'modules/data/data-selectors';
import {EPropertyCode} from 'modules/data/data-types';
import {getPopupContainer} from 'shared/utils';
import {checkDisabledActions} from 'shared/utils/request';
import {useAppSelector} from 'store/config/hooks';

import './checkbox-date-group.less';

export interface IBoolFieldProps {
    entityName: string;
    fieldKey: string;
    referenceUrl: string;
    propertyCodeList: string[];
    format?: string;
    form?: FormInstance;
    onChange?: (value?: string) => void;
    parentEntityName?: string;
    dependentsFieldKey?: string;
}

export const CheckboxDateGroup: FC<IBoolFieldProps> = ({
    format,
    propertyCodeList = [],
    parentEntityName,
    referenceUrl,
    onChange,
    form,
    dependentsFieldKey,
}: IBoolFieldProps) => {
    const rows = useAppSelector(selectTableEntityData(parentEntityName || ''))?.rows;
    const [disabledPublication, setDisabledPublication] = useState(false);
    const [disabledPublicationOnDate, setDisabledPublicationOnDate] = useState(false);
    const [resultValue, setResultValue] = useState<Moment | null>(null);
    const [value, setValue] = useState(1);
    const sectionCodeContext: string = String(rows?.slice(0, 1).shift()?.sectionCode);
    format = format ?? 'DD.MM.YYYY HH:mm:ss';

    const handleChange = (e: RadioChangeEvent) => {
        if (e.target.value === 1) {
            if (onChange) onChange(moment(new Date()).format());
            if (form && dependentsFieldKey) form.setFieldsValue({[dependentsFieldKey]: false});
            setResultValue(null);
        } else if (form && dependentsFieldKey) form.setFieldsValue({[dependentsFieldKey]: true});
        setValue(e.target.value);
    };

    const handleChangeDatePicker: DatePickerProps['onChange'] = date => {
        if (onChange) onChange(moment(date).format());
        setResultValue(date);
    };

    useEffect(() => {
        (async () => {
            const dataAll = await Promise.all(
                propertyCodeList?.map(async (propertyCode: string) => {
                    const {data} = await checkDisabledActions(referenceUrl, propertyCode, {sectionCodeContext});
                    return {data, propertyCode};
                }),
            );
            dataAll.forEach(data => {
                if (EPropertyCode.PUBLIC === data.propertyCode) setDisabledPublication(data.data);
                if (EPropertyCode.DELAYED_PUBLICATION === data.propertyCode) setDisabledPublicationOnDate(data.data);
            });
        })();
    }, [sectionCodeContext]);

    useEffect(() => {
        if ((disabledPublicationOnDate && disabledPublication) || (!disabledPublicationOnDate && disabledPublication)) {
            if (onChange) onChange(moment(new Date()).format());
            if (form && dependentsFieldKey) form.setFieldsValue({[dependentsFieldKey]: false});
            setValue(1);
        }
        if (disabledPublicationOnDate && !disabledPublication) {
            if (form && dependentsFieldKey) form.setFieldsValue({[dependentsFieldKey]: true});
            setValue(2);
        }
    }, [disabledPublication, disabledPublicationOnDate]);

    return (
        <Radio.Group
            onChange={handleChange}
            value={value}
            defaultValue={1}
            className="checkbox-date-group"
        >
            <Space direction="vertical">
                <Radio
                    value={1}
                    disabled={!disabledPublication}
                >
                    Незамедлительно
                </Radio>
                <Radio
                    disabled={!disabledPublicationOnDate}
                    value={2}
                >
                    На дату
                    {value === 2
                        ? (
                            <AntForm.Item
                                name="datePicker"
                                rules={[{required: true, message: 'Дата должна быть заполнена'}]}
                            >
                                <DatePicker
                                    format={format}
                                    showTime={format?.includes(' ')}
                                    onChange={handleChangeDatePicker}
                                    value={resultValue}
                                    className="date-picker"
                                    getPopupContainer={getPopupContainer()}
                                />
                            </AntForm.Item>
                        ) : <Input disabled />}
                </Radio>
            </Space>
        </Radio.Group>
    );
};
